import { useEffect, useState, useRef } from 'react';
import styles from './OnePager.module.scss';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import DealSummaryEditor from '../ManageDealDrawer/OnePagerGenerateButton/DealSummaryEditor/DealSummaryEditor';
import { initializeDatepickerDates } from '../../utils/dateUtils';
import {
    getOnePagerData,
    getOnePagerTemplateData,
    pollOnePagerStatus,
    saveOnePagerTemplateData,
} from '../../store/investor/investorApiHelper';
import {
    COMPANY_METRICS_DATA,
    initialiseDealSummaryEditor,
} from '../ManageDealDrawer/OnePagerGenerateButton/DealSummaryEditor/dealSummaryEditorInitializer';
import { closeOnePager, setOnePagerData } from '../../store/investor/action';
import { useParams } from 'react-router-dom';
import { Button, Modal } from '../_custom';
import { invokeOnePagerStateMachineService } from '../../store/investor/service';
import { getCompanyMetrics } from '../../store/debtProducts/apiHelper_debtProducts';
import { PROMISE_STATES } from '../../utils/axios-interceptor';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';

interface OnePagerPropsForCreateDeal {
    metadataUpdate?: (fileInfo) => void;
    categoryId?: number;
    flowId?: number;
    companyName?: string;
}

type OnePagerProps = OnePagerPropsForCreateDeal;

function OnePager({ metadataUpdate, categoryId, flowId, companyName }: OnePagerProps) {
    const dispatch = useAppDispatch();
    const params = useParams();
    const [generated, setGenerated] = useState(false);
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);
    const [pollingStatus, setPollingStatus] = useState<'PENDING' | 'COMPLETE' | 'FAILED' | null>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const pollingCountRef = useRef(0);
    const requestIdRef = useRef<string>('');

    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const onePagerState = useAppSelector((state) => state.investor.onePager);
    const assesseeOrgId = onePagerState.assesseeOrgId;
    const dealFormData = onePagerState.dealFormData;
    const source = onePagerState.source;
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);

    useEffect(() => {
        return () => {
            dispatch(closeOnePager());
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, []);

    useEffect(() => {
        fetchOnePagerTemplateData();
    }, [assesseeOrgId]);

    useEffect(() => {
        if (editorInitialData) {
            setEditorReady(true);
        }
    }, [editorInitialData]);

    const startPolling = () => {
        if (!pollingRef.current) {
            pollingRef.current = setInterval(() => {
                pollModuleStatus();
            }, 5000);
        }
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        pollingCountRef.current = 0;
    };

    const pollModuleStatus = async () => {
        if (pollingCountRef.current >= 24) {
            // 2 minutes max (24 * 5000ms)
            stopPolling();
            setPollingStatus('FAILED');
            return;
        }

        try {
            const onePagerStatus = await pollOnePagerStatus({ orgId: assesseeOrgId });
            // Check the stateMachineStatus from the response
            if (onePagerStatus?.stateMachineStatus === 'COMPLETED' || onePagerStatus === null) {
                stopPolling();
                setPollingStatus('COMPLETE');
                // Once polling is complete, fetch the actual one pager data
                fetchOnePagerDataAndCompanyMetrics();
            } else if (onePagerStatus?.stateMachineStatus === 'FAILED') {
                stopPolling();
                setPollingStatus('FAILED');
                // If polling returns failed status, go ahead with one pager data fetch
                fetchOnePagerDataAndCompanyMetrics();
            } else if (onePagerStatus?.stateMachineStatus === 'PENDING') {
                // Continue polling
                pollingCountRef.current++;
            }
        } catch (error) {
            console.error('Polling error:', error);
            pollingCountRef.current++;
        }
    };

    const invokeModuleSummaryStateMachine = async () => {
        try {
            setPollingStatus('PENDING');
            const timestamp = new Date().getTime();
            requestIdRef.current = `onepager_${timestamp}`;

            await invokeOnePagerStateMachineService({
                requestId: requestIdRef.current,
                orgId: assesseeOrgId,
            });

            startPolling();
        } catch (error) {
            console.error('State machine invocation error:', error);
            setPollingStatus('FAILED');
        }
    };

    const fetchOnePagerDataAndCompanyMetrics = async () => {
        const [fromDate, toDate] = initializeDatepickerDates('Monthly');
        let [onePagerDataResponse, companyMetricsResponse] = await Promise.allSettled([
            getOnePagerData({
                orgId: assesseeOrgId as string,
                // startDate: "2025-05-01",
                // endDate: toDate,
            }),
            getCompanyMetrics({ assesseeOrgId: assesseeOrgId as string }),
        ]);
        let onePagerData = onePagerDataResponse.status === PROMISE_STATES.FULFILLED ? onePagerDataResponse.value : {};
        let companyMetricsData =
            companyMetricsResponse.status === PROMISE_STATES.FULFILLED
                ? companyMetricsResponse.value?.companyMetrics || companyMetricsResponse.value || {}
                : {};

        onePagerData[COMPANY_METRICS_DATA] = companyMetricsData;
        setEditorInitialData(sanitizeSlateValue(initialiseDealSummaryEditor(onePagerData, dealFormData || {})));
        setTimeout(() => {
            setEditorReady(true);
            dispatch(setOnePagerData({ loading: false }));
        }, 200);
    };

    const fetchDataForOnePager = (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        dispatch(setOnePagerData({ loading: true }));
        invokeModuleSummaryStateMachine();
    };

    const fetchOnePagerTemplateData = async () => {
        setEditorReady(false);
        dispatch(setOnePagerData({ loading: true }));
        const onePagerTemplateResponse = await getOnePagerTemplateData({
            assesseeOrgId: assesseeOrgId as string,
            investorOrgId: investorOrgId,
        });
        const { onePagerData, templateData } = onePagerTemplateResponse;
        if (templateData?.length) {
            // Sanitize template data to handle old structures (table-body, invalid lists, etc.)
            const sanitizedData = sanitizeSlateValue(templateData);
            setEditorInitialData(sanitizedData);
            setUserInfo(onePagerData);
            setTimeout(() => {
                setEditorReady(true);
                dispatch(setOnePagerData({ loading: false }));
            }, 200);
        } else fetchDataForOnePager();
    };

    const saveOnePagerTemplateDataForOrg = (editorContent, isRestored = false, onSuccess) => {
        if (savingData) return;
        setSavingData(true);
        saveOnePagerTemplateData({
            assesseeOrgId: assesseeOrgId as string,
            investorOrgId: investorOrgId,
            onePagerData: editorContent || [],
            email: emailId as string,
            isRestored,
        })
            .then((data) => {
                setTimeout(() => {
                    if (!isRestored) {
                        dispatch(closeOnePager());
                    }
                    setSavingData(false);
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 1000);
            })
            .catch(() => {
                setSavingData(false);
            });
    };

    const onMetadataUpdateFunction = (fileInfo) => {
        metadataUpdate?.(fileInfo);
    };

    return (
        <div className={styles.OnePager}>
            {(source === 'CREATE_DEAL' ? onePagerState.open : editorReady) && (
                <DealSummaryEditor
                    source={source}
                    generating={onePagerState.loading}
                    openModal={source === 'CREATE_DEAL' ? true : editorReady}
                    closeModal={() => dispatch(closeOnePager())}
                    categoryId={categoryId}
                    flowId={flowId}
                    assesseeOrgId={assesseeOrgId}
                    metadataUpdate={onMetadataUpdateFunction}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    savingData={savingData}
                    saveOnePagerTemplateDataForOrg={saveOnePagerTemplateDataForOrg}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    userInfo={userInfo}
                    companyNameProp={companyName}
                />
            )}

            {showRegenConfirmModal && (
                <Modal open={showRegenConfirmModal} onClose={() => setShowRegenConfirmModal(false)} size="flexible">
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate One Pager</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate One pager? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                }}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForOnePager(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default OnePager;
